import { useSelector } from "react-redux";
import { createSlice } from "@reduxjs/toolkit";

import { RootState, PropertiesState } from "@/types/store";
import {
  getMapPins,
  getMoreProperties,
  getMorePropertiesForUser,
  getProperties,
  getPropertiesForUser,
  getPropertyById,
} from "@/api/properties/thunks";
import { FilterCityState, FilterHomeowner, FilterProperty, FilterStatus, Property } from "@/types/properties";
import { DEFAULT_BEDROOM_COUNT, PROPERTY_STATUS_FILTER_OPTIONS } from "@/utils/constants";

// Dedup key: use streamlineUnitId for Streamline-synced properties, otherwise id
const getPropertyDedupeKey = (property: Property): string =>
  (property as Property & { streamlineUnitId?: string | null }).streamlineUnitId || property.id;

const initialState: PropertiesState = {
  properties: [],
  total: 0,
  mapPins: [],
  loadingProperties: false,
  error: null,
  loadingIndividualProperty: false,
  propertiesPage: 1,
  propertiesEndReached: false,
  filterProperties: [] as FilterProperty[],
  filterHomeowners: [] as FilterHomeowner[],
  filterStatuses: PROPERTY_STATUS_FILTER_OPTIONS.map((s) => ({ ...s })) as FilterStatus[],
  filterCityStates: [] as FilterCityState[],
  filterBedroomCount: DEFAULT_BEDROOM_COUNT,
  propertyTagIds: [],
};

export const PropertiesSlice = createSlice({
  name: "PropertiesSlice",
  initialState,
  reducers: {
    addFilterProperty: (state, { payload }) => {
      state.filterProperties = [...state.filterProperties, payload];
    },
    removeFilterProperty: (state, { payload }) => {
      state.filterProperties = state.filterProperties.filter(
        (property: FilterProperty) => property.id !== payload.id
      );
    },
    addFilterHomeowner: (state, { payload }) => {
      state.filterHomeowners = [...state.filterHomeowners, payload];
    },
    removeFilterHomeowner: (state, { payload }) => {
      state.filterHomeowners = state.filterHomeowners.filter(
        (homeowner: FilterHomeowner) => homeowner.id !== payload.id
      );
    },
    setFilterStatuses: (state, { payload }) => {
      state.filterStatuses = payload;
    },
    setFilterCityStates: (state, { payload }) => {
      state.filterCityStates = payload;
    },
    setFilterBedroomCount: (state, { payload }) => {
      state.filterBedroomCount = payload;
    },
    setFilterProperties: (state, { payload }) => {
      state.filterProperties = payload;
    },
    setFilterHomeowners: (state, { payload }) => {
      state.filterHomeowners = payload;
    },
    setAllPropertyFilters: (state, { payload }) => {
      state.filterStatuses = payload.statuses;
      state.filterHomeowners = payload.homeowners;
      state.filterProperties = payload.properties;
      state.filterCityStates = payload.cityStates;
      state.filterBedroomCount = payload.numberOfBedrooms;
      state.propertyTagIds = payload.propertyTagIds ?? [];
      state.propertiesPage = 1;
      state.propertiesEndReached = false;
    },
    clearAllPropertyFilters: (state) => {
      state.filterStatuses = PROPERTY_STATUS_FILTER_OPTIONS.map((s) => ({ ...s, selected: false }));
      state.filterHomeowners = [];
      state.filterProperties = [];
      state.filterCityStates = [];
      state.filterBedroomCount = DEFAULT_BEDROOM_COUNT;
      state.propertyTagIds = [];
      state.propertiesPage = 1;
      state.propertiesEndReached = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProperties.pending, (state) => {
        state.loadingProperties = true;
        state.error = null;
      })
      .addCase(getProperties.fulfilled, (state, { payload }) => {
        state.loadingProperties = false;
        // Deduplicate by streamlineUnitId to handle multi-owner Streamline properties
        const seen = new Set<string>();
        state.properties = payload.properties.filter(
          (property: Property) => {
            const key = getPropertyDedupeKey(property);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          }
        );
        state.total = payload.total;
        state.propertiesPage = 1;
        state.propertiesEndReached = !payload.hasNextPage;
      })
      .addCase(getProperties.rejected, (state, action) => {
        state.loadingProperties = false;
        state.error = action.payload as string;
      })
      .addCase(getMoreProperties.fulfilled, (state, { payload }) => {
        state.loadingProperties = false;
        const existingKeys = new Set(
          state.properties.map((p: Property) => getPropertyDedupeKey(p))
        );
        const newProperties: Property[] = [];
        for (const property of payload.properties) {
          const key = getPropertyDedupeKey(property);
          if (!existingKeys.has(key)) {
            existingKeys.add(key);
            newProperties.push(property);
          }
        }
        state.properties = [...state.properties, ...newProperties];
        state.propertiesPage += 1;
        state.propertiesEndReached = !payload.hasNextPage;
      })
      .addCase(getPropertiesForUser.fulfilled, (state, { payload }) => {
        state.loadingProperties = false;
        // Deduplicate by streamlineUnitId to handle multi-owner Streamline properties
        const seen = new Set<string>();
        state.properties = payload.properties.filter(
          (property: Property) => {
            const key = getPropertyDedupeKey(property);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          }
        );
        state.propertiesPage = 1;
        state.propertiesEndReached = !payload.hasNextPage;
      })
      .addCase(getPropertiesForUser.rejected, (state, action) => {
        state.loadingProperties = false;
        state.error = action.payload as string;
      })
      .addCase(getMorePropertiesForUser.fulfilled, (state, { payload }) => {
        state.loadingProperties = false;
        const existingKeys = new Set(
          state.properties.map((p: Property) => getPropertyDedupeKey(p))
        );
        const newProperties: Property[] = [];
        for (const property of payload.properties) {
          const key = getPropertyDedupeKey(property);
          if (!existingKeys.has(key)) {
            existingKeys.add(key);
            newProperties.push(property);
          }
        }
        state.properties = [...state.properties, ...newProperties];
        state.propertiesPage += 1;
        state.propertiesEndReached = !payload.hasNextPage;
      })
      .addCase(getPropertyById.pending, (state) => {
        state.loadingIndividualProperty = true;
      })
      .addCase(getPropertyById.fulfilled, (state) => {
        state.loadingIndividualProperty = false;
      })
      .addCase(getPropertyById.rejected, (state) => {
        state.loadingIndividualProperty = false;
      })
      .addCase(getMapPins.fulfilled, (state, { payload }) => {
        state.mapPins = payload;
      });
  },
});

export const {
  addFilterProperty,
  removeFilterProperty,
  addFilterHomeowner,
  removeFilterHomeowner,
  setFilterStatuses,
  setFilterCityStates,
  setFilterBedroomCount,
  setFilterProperties,
  setFilterHomeowners,
  setAllPropertyFilters,
  clearAllPropertyFilters,
} = PropertiesSlice.actions;

export default PropertiesSlice.reducer;

export const usePropertiesData = () => {
  const properties = useSelector(
    (state: RootState) => state.Properties.properties
  );
  const total = useSelector((state: RootState) => state.Properties.total);
  const loadingProperties = useSelector(
    (state: RootState) => state.Properties.loadingProperties
  );
  const loadingIndividualProperty = useSelector(
    (state: RootState) => state.Properties.loadingIndividualProperty
  );
  const error = useSelector((state: RootState) => state.Properties.error);
  const propertiesPage = useSelector(
    (state: RootState) => state.Properties.propertiesPage
  );
  const propertiesEndReached = useSelector(
    (state: RootState) => state.Properties.propertiesEndReached
  );
  const filterProperties = useSelector(
    (state: RootState) => state.Properties.filterProperties
  );
  const filterHomeowners = useSelector(
    (state: RootState) => state.Properties.filterHomeowners
  );
  const mapPins = useSelector((state: RootState) => state.Properties.mapPins);
  const filterStatuses = useSelector(
    (state: RootState) => state.Properties.filterStatuses
  );
  const filterCityStates = useSelector(
    (state: RootState) => state.Properties.filterCityStates
  );
  const filterBedroomCount = useSelector(
    (state: RootState) => state.Properties.filterBedroomCount
  );
  const propertyTagIds = useSelector(
    (state: RootState) => state.Properties.propertyTagIds
  );

  return {
    properties,
    total,
    mapPins,
    loadingProperties,
    error,
    loadingIndividualProperty,
    propertiesPage,
    propertiesEndReached,
    filterProperties,
    filterHomeowners,
    filterStatuses,
    filterCityStates,
    filterBedroomCount,
    propertyTagIds,
  };
};
